import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
  Button,
  Grid,
  Modal,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  useBanner,
  useThemedValueSwitch,
} from '@tq/ui-components';

import {
  useUpdateDfiAnnualReport,
  useSetDataDfiAnnualReportCompleted,
  UpdateDfiReportRequest,
} from 'api';
import { dfiReportUpdateValuesState } from 'store';
import { DfiReportMetrics, DfiReportResponse } from 'types';
import { BANNER_TIMEOUT } from 'config';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { MobileModal, ThemeProvider } from 'components';
import { CompletionModalContent, StaticExportDfiReport, WrapStyledHtmlTemplate } from './';
import { renderToStaticMarkup } from 'react-dom/server';

type CompletionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  report: DfiReportResponse;
  heading: string;
};

export const CompletionModal: React.FC<CompletionModalProps> = ({
  isOpen,
  onClose,
  report,
  heading,
}) => {
  const { t } = useTranslation('report_completion_modal');
  const banner = useBanner();
  const isMobile = useIsMobile();

  const { isTQ } = useThemedValueSwitch();

  const updateDfiReportStoreValues = useRecoilValue(dfiReportUpdateValuesState);
  const { mutate: updateDfiAnnualMutate, isLoading } = useUpdateDfiAnnualReport();
  const setDfiReportQueryDataCompleted = useSetDataDfiAnnualReportCompleted();

  const handleCancel = () => {
    onClose();
  };

  const submitReport = () => {
    const metrics: DfiReportMetrics = {
      statutoryTimelinessResponseStats: report.statutoryTimelinessResponseStats,
      lateResponseStatItems: report.lateResponseStatItems,
      statutoryTimeframeExceededStats: report.statutoryTimeframeExceededStats,
      agreedExtensionStats: report.agreedExtensionStats,
      nonStatutoryTimelinessResponseStats: report.nonStatutoryTimelinessResponseStats,
      eiaConsultationStats: report.eiaConsultationStats,
      tqEnTimelinessResponseStats: report.tqEnTimelinessResponseStats,
      tqEnTimeframeExceededStats: report.tqEnTimeframeExceededStats,
      tqEnAgreedExtensionStats: report.tqEnAgreedExtensionStats,
      tqEnAppealConsultationStats: report.tqEnAppealConsultationStats,
    };

    if (isTQ) {
      metrics.statutoryTimelinessResponseStats = metrics.tqEnTimelinessResponseStats;
      metrics.statutoryTimeframeExceededStats = metrics.tqEnTimeframeExceededStats;
      metrics.agreedExtensionStats = metrics.tqEnAgreedExtensionStats;
      metrics.eiaConsultationStats = metrics.tqEnAppealConsultationStats;
    }

    const htmlBody = renderToStaticMarkup(
      <ThemeProvider>
        <StaticExportDfiReport heading={heading} report={report} />
      </ThemeProvider>
    );

    const data: UpdateDfiReportRequest = {
      ...updateDfiReportStoreValues,
      completed: true,
      metrics: JSON.stringify(metrics),
      html: WrapStyledHtmlTemplate(htmlBody),
    };

    updateDfiAnnualMutate(data, {
      onSuccess: () => {
        banner({
          id: 'submit-dfi-report-banner-success',
          title: t('report_submitted', 'Report complete'),
          duration: BANNER_TIMEOUT,
        });

        setDfiReportQueryDataCompleted(report);

        onClose();
      },
      onError: () => {
        banner({
          id: 'submit-dfi-report-banner-fail',
          title: t('report_submission_failed', 'Could not complete report. Please try again'),
          duration: BANNER_TIMEOUT,
          status: 'error',
        });
        onClose();
      },
    });
  };

  return isMobile ? (
    <MobileModal isOpen={isOpen} onClose={handleCancel}>
      <CompletionModalContent isLoading={isLoading} />
      <ModalFooter>
        <Grid gridGap={4} w='100%'>
          <Button isFullWidth onClick={submitReport} disabled={isLoading}>
            {t('complete_button_text', 'Complete')}
          </Button>
          <Button isFullWidth variant='tertiary' onClick={handleCancel} disabled={isLoading}>
            {t('cancel_button_text', 'Cancel')}
          </Button>
        </Grid>
      </ModalFooter>
    </MobileModal>
  ) : (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <ModalOverlay />
      <ModalContent>
        <CompletionModalContent isLoading={isLoading} />
        <ModalFooter justifyContent='end'>
          <Grid gridAutoFlow='column' gridGap={4}>
            <Button variant='tertiary' onClick={handleCancel} disabled={isLoading}>
              {t('cancel_button_text', 'Cancel')}
            </Button>
            <Button onClick={submitReport} disabled={isLoading}>
              {t('complete_button_text', 'Complete')}
            </Button>
          </Grid>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
